/**
 * Skeleton — reusable shimmer primitives.
 *
 * Industry pattern: small, composable, no layout opinions.
 * Caller owns the grid/flex layout. Skeleton owns only the shimmer shape.
 *
 * Uses the shimmer keyframe already defined in global.css:
 *   .table-list-skeleton-bar  →  gradient + animation
 *
 * Primitives (free-form sizing):
 *   <Skeleton.Box width="60%" height={14} radius={6} />
 *   <Skeleton.Circle size={44} />
 *   <Skeleton.Text lines={2} />
 *   <Skeleton.Pill width={80} height={22} />
 *
 * Table rows (pass col widths, renders 8 <tr> skeleton rows):
 *   <Skeleton.TableRows cols={['2xs', 'md', 'sm', 'pill', 'icon']} />
 *
 *   Col tokens: '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'pill' | 'icon'
 *   Center-align a col:  { width: 'xs', align: 'center' }
 *   Right-align a col:   { width: 'icon', align: 'right' }
 *   Multi-icon actions:  { width: 'icon', align: 'right', count: 2 }
 *
 * Tree rows (same API, renders tree-list skeleton rows):
 *   <Skeleton.TreeRows cols={['sm', 'md', 'pill', 'icon']} />
 */

import React from "react";

// ─── Box: the single shimmer primitive everything else is built from ──────────

interface BoxProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  style?: React.CSSProperties;
  className?: string;
}

const Box: React.FC<BoxProps> = ({
  width = "100%",
  height = 12,
  radius = 4,
  style,
  className = "",
}) => (
  <div
    className={`table-list-skeleton-bar ${className}`}
    style={{ width, height, borderRadius: radius, flexShrink: 0, ...style }}
    aria-hidden="true"
  />
);

// ─── Circle: avatar, icon placeholder ────────────────────────────────────────

interface CircleProps {
  size?: number;
  style?: React.CSSProperties;
  className?: string;
}

const Circle: React.FC<CircleProps> = ({ size = 40, style, className = "" }) => (
  <Box width={size} height={size} radius="50%" style={style} className={className} />
);

// ─── Text: one or more text line placeholders ─────────────────────────────────

interface TextProps {
  lines?: number;
  height?: number;
  gap?: number;
  /** Width of the last line — shorter last line looks more natural */
  lastWidth?: number | string;
  style?: React.CSSProperties;
  className?: string;
}

const Text: React.FC<TextProps> = ({
  lines = 1,
  height = 12,
  gap = 8,
  lastWidth = "65%",
  style,
  className = "",
}) => (
  <div
    className={className}
    style={{ display: "flex", flexDirection: "column", gap, ...style }}
    aria-hidden="true"
  >
    {Array.from({ length: lines }).map((_, i) => (
      <Box
        key={i}
        height={height}
        width={i === lines - 1 && lines > 1 ? lastWidth : "100%"}
      />
    ))}
  </div>
);

// ─── Pill: badge / status chip placeholder ────────────────────────────────────

interface PillProps {
  width?: number | string;
  height?: number;
  style?: React.CSSProperties;
}

const Pill: React.FC<PillProps> = ({ width = 72, height = 22, style }) => (
  <Box width={width} height={height} radius={9999} style={style} />
);

// ─── TableRows / TreeRows ─────────────────────────────────────────────────────

type ColWidth = '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'pill' | 'icon' | 'dot';
type ColAlign = 'left' | 'center' | 'right';

type ColDef =
  | ColWidth
  | { width: ColWidth; align?: ColAlign; count?: number };

const resolveCol = (col: ColDef): { width: ColWidth; align: ColAlign; count: number } => {
  if (typeof col === 'string') return { width: col, align: 'left', count: 1 };
  return { width: col.width, align: col.align ?? 'left', count: col.count ?? 1 };
};

const alignClass: Record<ColAlign, string> = {
  left: '',
  center: 'skel-center',
  right: 'skel-right',
};

interface RowsProps {
  cols: ColDef[];
  rows?: number;
  rowClassName?: string;
  cellClassName?: string;
}

const TableRows: React.FC<RowsProps> = ({
  cols,
  rows = 8,
  rowClassName = 'table-list-skeleton-row',
  cellClassName = 'table-list-skeleton-cell',
}) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className={rowClassName}>
        {cols.map((col, j) => {
          const { width, align, count } = resolveCol(col);
          // Size tokens map to the `--w-*` width modifiers in skeleton.css;
          // shape tokens (pill / icon) use the bare `--<token>` class. Keeping
          // these names in sync is what makes the skeleton bars line up with
          // the real column widths.
          const barClass = width === 'dot'
            ? 'table-list-skeleton-bar skel-dot'
            : width === 'pill' || width === 'icon'
              ? `table-list-skeleton-bar table-list-skeleton-bar--${width}`
              : `table-list-skeleton-bar table-list-skeleton-bar--w-${width}`;
          // The dot column has a fixed 40px width + centering via the shared
          // `table-list-cell--dot` class; apply it here so the skeleton dot
          // lines up with the real workflow-dot column instead of the wider
          // default skeleton cell padding.
          const cellClass = width === 'dot'
            ? `${cellClassName} table-list-cell--dot`
            : cellClassName;
          return (
            <td key={j} className={cellClass}>
              {count > 1 ? (
                <div className="skel-icon-group">
                  {Array.from({ length: count }).map((_, k) => (
                    <div key={k} className={barClass} />
                  ))}
                </div>
              ) : (
                <div className={`${barClass}${alignClass[align] ? ` ${alignClass[align]}` : ''}`} />
              )}
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

const TreeRows: React.FC<RowsProps> = (props) => (
  <TableRows
    {...props}
    rowClassName={props.rowClassName ?? 'table-list-skeleton-row'}
    cellClassName={props.cellClassName ?? 'table-list-skeleton-cell'}
  />
);

// ─── Composed export ──────────────────────────────────────────────────────────

const Skeleton = { Box, Circle, Text, Pill, TableRows, TreeRows };
export default Skeleton;
